import { useState, useEffect, useCallback, useRef } from "react";
import { InfoClient, SubscriptionClient } from "../sdk";
import { ConnectionState } from "../sdk/types/transport";
import {
  RawBucket,
  MultiFeedBucketsData,
  MultiFeedBucketData,
} from "../sdk/types/api";
import {
  Bucket,
  BucketsConfig,
  transformRawBucketToBucket,
  convertMultiFeedToRawBucket,
  isRawBucket,
} from "../sdk/types/data";

// Re-export for backward compatibility
export type { Bucket, BucketsConfig };

/**
 * Hook for managing buckets data for a wallet address
 * Uses WebSocket as primary source with HTTP fallback
 * Maintains backward compatibility with polling approach
 */
export function useBuckets(config: BucketsConfig) {
  const useWebSocket = config.useWebSocket !== false; // Default: true
  const [buckets, setBuckets] = useState<Bucket[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const infoClientRef = useRef<InfoClient | null>(null);
  const subscriptionClientRef = useRef<SubscriptionClient | null>(null);
  const refreshIntervalRef = useRef<number>(0);
  const isSubscribedRef = useRef<boolean>(false);
  const currentMarketIdRef = useRef<string | null>(null);
  const currentSymbolRef = useRef<string | null>(null);

  /**
   * Transform bucket data to Bucket format
   * Handles both RawBucket and MultiFeedBucketData types
   */
  const transformBucket = useCallback(
    (bucket: RawBucket | MultiFeedBucketData): Bucket => {
      // Convert MultiFeedBucketData to RawBucket if needed
      const rawBucket: RawBucket = isRawBucket(bucket)
        ? bucket
        : convertMultiFeedToRawBucket(bucket);

      // Transform raw API data to Bucket type (handles satoshi conversion)
      return transformRawBucketToBucket(rawBucket);
    },
    [],
  );

  /**
   * Fetch buckets from API
   */
  const fetchBuckets = useCallback(async () => {
    if (!infoClientRef.current) return;

    setError("");

    try {
      if (!config.walletAddress) {
        setError("Wallet address is required");
        setBuckets([]);
        return;
      }

      // Fetch buckets from API
      const response = await infoClientRef.current.getBuckets(
        config.walletAddress,
      );

      const bucketsData = Array.isArray(response) ? response : [];

      if (!Array.isArray(response)) {
        console.warn("[useBuckets] getBuckets returned non-array:", response);
      }

      // Transform API response to Bucket format
      // Uses shared transformation function that handles satoshi conversion
      const transformedBuckets: Bucket[] = bucketsData.map(transformBucket);

      // Compare with current buckets to avoid unnecessary state updates
      setBuckets((prev) => {
        if (prev.length !== transformedBuckets.length) {
          return transformedBuckets;
        }
        // Deep comparison - only update if buckets actually changed
        const prevStr = JSON.stringify(prev);
        const newStr = JSON.stringify(transformedBuckets);
        return prevStr === newStr ? prev : transformedBuckets;
      });
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to load buckets";
      setError(errorMessage);
      console.error("Failed to fetch buckets:", err);
    } finally {
      setLoading(false);
    }
  }, [config.walletAddress, transformBucket]);

  /**
   * Handle bucket updates from WebSocket (multi-feed buckets sub-channel)
   */
  const handleBucketsUpdate = useCallback(
    (data: MultiFeedBucketsData) => {
      if (data.buckets && Array.isArray(data.buckets)) {
        const transformedBuckets: Bucket[] = data.buckets.map(transformBucket);

        // Compare with current buckets to avoid unnecessary state updates
        setBuckets((prev) => {
          if (prev.length !== transformedBuckets.length) {
            return transformedBuckets;
          }
          // Deep comparison - only update if buckets actually changed
          const prevStr = JSON.stringify(prev);
          const newStr = JSON.stringify(transformedBuckets);
          return prevStr === newStr ? prev : transformedBuckets;
        });
        setLoading(false);
      }
    },
    [transformBucket],
  );

  // Initialize clients and set up WebSocket or polling
  useEffect(() => {
    const infoClient = new InfoClient({
      baseURL: config.baseURL,
      sessionToken: config.authToken,
    });
    infoClientRef.current = infoClient;

    // Initial fetch from HTTP (always do this for fast initial load)
    fetchBuckets();

    const subscriptionClient = config.subscriptionClient;

    if (useWebSocket) {
      // WebSocket mode: primary source
      subscriptionClientRef.current = subscriptionClient;

      // Subscribe function that can be called on initial connect or reconnection
      const subscribe = () => {
        // Check if ready for subscriptions (connected and authenticated if required)
        if (!subscriptionClient.isReadyForSubscriptions()) {
          if (
            subscriptionClient.isConnected() &&
            !subscriptionClient.isReadyForSubscriptions()
          ) {
            // Connected but not authenticated - wait a bit and retry
            const authState = subscriptionClient.getAuthState();
            if (authState === "pending") {
              console.debug(
                "[useBuckets] Waiting for authentication to complete...",
              );
              // Will retry on next state change
            } else if (authState === "failed") {
              console.error(
                "[useBuckets] Authentication failed - cannot subscribe to buckets",
              );
              setError("Authentication failed - cannot subscribe to buckets");
            }
          }
          return;
        }

        // Check if we need to re-subscribe (marketId or symbol changed, or not yet subscribed)
        const marketId = String(config.marketId ?? "0"); // Default to "0" for user-global
        const symbol = config.symbol ?? "USDC/USDC"; // Default symbol for user-global
        const needsResubscribe =
          !isSubscribedRef.current ||
          currentMarketIdRef.current !== marketId ||
          currentSymbolRef.current !== symbol;

        if (needsResubscribe) {
          // Unsubscribe from previous subscription if it exists
          if (isSubscribedRef.current && subscriptionClientRef.current) {
            try {
              subscriptionClientRef.current.unsubscribeMultiFeedSubChannel(
                "buckets",
                handleBucketsUpdate,
              );
            } catch (error) {
              console.warn(
                "[useBuckets] Error unsubscribing from previous buckets subscription:",
                error,
              );
            }
          }

          try {
            // Use multi-feed sub-channel for buckets
            subscriptionClient.subscribeMultiFeedSubChannel(
              "buckets",
              marketId,
              handleBucketsUpdate,
              { symbol },
            );
            isSubscribedRef.current = true;
            currentMarketIdRef.current = marketId;
            currentSymbolRef.current = symbol;
            setLoading(false);
            console.debug("[useBuckets] Subscribed to buckets:", {
              marketId,
              symbol,
            });
          } catch (error) {
            console.error(
              "[useBuckets] Failed to subscribe to buckets:",
              error,
            );
            setError(
              error instanceof Error
                ? error.message
                : "Failed to subscribe to buckets",
            );
            isSubscribedRef.current = false;
            setLoading(false);
          }
        }
      };

      // Set initial connection state
      setIsConnected(subscriptionClient.isConnected());
      setLoading(false);

      // Try to subscribe immediately if client is ready
      if (subscriptionClient.isReadyForSubscriptions()) {
        subscribe();
      }

      // Monitor connection state changes and re-subscribe on reconnection
      const unsubscribeStateChange = subscriptionClient.onStateChange(
        (state: ConnectionState) => {
          console.debug("[useBuckets] WebSocket state changed to:", state);
          setIsConnected(state === ConnectionState.Connected);
          if (state === ConnectionState.Connected) {
            // Always try to subscribe on connection (subscribe function handles re-subscription logic)
            subscribe();
            // Stop polling when WS is connected
            if (refreshIntervalRef.current !== null) {
              clearInterval(refreshIntervalRef.current);
              refreshIntervalRef.current = 0;
            }
          } else if (
            state === ConnectionState.Disconnected ||
            state === ConnectionState.Reconnecting
          ) {
            // Fallback to polling when WS is disconnected
            if (config.refreshInterval && config.refreshInterval > 0) {
              if (refreshIntervalRef.current === null) {
                refreshIntervalRef.current = window.setInterval(() => {
                  fetchBuckets();
                }, config.refreshInterval);
              }
            }
          }
        },
      );

      return () => {
        // Unsubscribe from state changes
        if (unsubscribeStateChange) {
          unsubscribeStateChange();
        }

        if (refreshIntervalRef.current !== null) {
          clearInterval(refreshIntervalRef.current);
          refreshIntervalRef.current = 0;
        }

        // Unsubscribe from buckets sub-channel
        if (subscriptionClientRef.current && isSubscribedRef.current) {
          try {
            subscriptionClientRef.current.unsubscribeMultiFeedSubChannel(
              "buckets",
              handleBucketsUpdate,
            );
          } catch (error) {
            console.warn(
              "[useBuckets] Error unsubscribing during cleanup:",
              error,
            );
          }
        }

        // Reset subscription state
        isSubscribedRef.current = false;
        currentMarketIdRef.current = null;
        currentSymbolRef.current = null;
      };
    } else {
      // Polling mode: fallback or when useWebSocket is false
      if (config.refreshInterval && config.refreshInterval > 0) {
        refreshIntervalRef.current = window.setInterval(() => {
          fetchBuckets();
        }, config.refreshInterval);
      }

      return () => {
        if (refreshIntervalRef.current !== null) {
          clearInterval(refreshIntervalRef.current);
          refreshIntervalRef.current = 0;
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    config.subscriptionClient,
    config.baseURL,
    config.authToken,
    config.walletAddress,
    config.refreshInterval,
    config.marketId,
    config.symbol,
    useWebSocket,
    // Note: fetchBuckets and handleBucketsUpdate are intentionally NOT in dependencies
    // fetchBuckets uses config.walletAddress via closure
    // handleBucketsUpdate is stable and subscription is managed separately
  ]);

  return {
    buckets,
    loading,
    error,
    isConnected,
    refresh: fetchBuckets,
  };
}
